import { Injectable, Injector, NgZone } from '@angular/core';
import { HttpRequest } from '@angular/common/http';

import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseHttpService } from './base-http.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { User } from '../models/user';
import { Menu } from '../models/rights/menu';
import { IMenuStructure } from '../models/rights/i-menu-structure';

const CHECK_INTERVALL = 1000 // in ms
const STORE_KEY = 'autoLogoutLastActivity';

@Injectable()
export class AuthenticationService extends BaseHttpService {
    authLoginCtlrAdress = "auth/login";
    settingsCtlrAdress = "settings";
    loginPageRoute = "login";
    processDetails: (details) => void;

    private _isAutoLogOut = false;
    private _minutesBeforeAutoLogout = 12 * 60; // 12 hours by default

    private _isLogged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    isLogged: Observable<boolean> = this._isLogged.asObservable();

    private readonly _storage: Storage = sessionStorage;

    get lastActivity() {
        return parseInt(localStorage.getItem(STORE_KEY));
    }
    set lastActivity(value) {
        localStorage.setItem(STORE_KEY, value.toString());
    }

    constructor(_injector: Injector, private _ngZone: NgZone) {
        super(_injector);
    }

    currentUser: User;

    private _menus: Menu[];
    get menus(): Menu[] { return this._menus; }
    set menus(menus: Menu[]) {
        this._menus = this.convertToT(Menu, menus) as Menu[];
        this._flattenMenus = [];
        for (let menu of this._menus) this.addFlattenPath(menu);
    }

    private addFlattenPath(structure: IMenuStructure) {
        this._flattenMenus.push({ path: structure.structurePath, isOwned: structure.isOwned });
        if (structure.children) for (let child of structure.children) this.addFlattenPath(child);
    }

    private _flattenMenus: { path: string, isOwned: boolean }[];
    get flattenMenus(): { path: string, isOwned: boolean }[] { return this._flattenMenus; }

    login(credential: { login: string, password: string }): Observable<any> {
        const loginRequest: HttpRequest<any> = this.getRequest(this.authLoginCtlrAdress, "POST", null, JSON.stringify({ userName: credential.login, password: credential.password }));

        return this.executeQuery<any>(loginRequest).pipe(
            map(res => {
                const data = {
                    passwordDefined: !!credential.password,
                    token: res.auth_token
                };
                this._storage.setItem(location.origin, JSON.stringify(data));

                this.storeDetails(res.details);

                const getSettingsRequest = this.getRequest(this.settingsCtlrAdress + '/AppSettings.MaxInactivityTime', "GET");
                this.executeQuery<any>(getSettingsRequest).subscribe(res2 => {
                    this.activateAutoLogout(res2);
                }, () => this.activateAutoLogout());

                return res;
            }));
    }

    logout() {
        this.deactivateAutoLogout();
        this._storage.removeItem(location.origin);
        this.currentUser = undefined;
        this._isLogged.next(false);
        this.navigate(this.loginPageRoute);
    }

    isLoggedIn(): Promise<boolean> {
        const raw: string = this._storage.getItem(location.origin);
        if (!raw) return Promise.resolve(false);

        const data: { token: string, passwordDefined: boolean } = JSON.parse(raw);

        const token: string = data.token;
        if (!token) return Promise.resolve(false);

        if (!data.passwordDefined) return Promise.resolve(false);

        if (this.currentUser) {
            this._isLogged.next(true);
            return Promise.resolve(true);
        }

        const helper = new JwtHelperService();
        const decodedToken = helper.decodeToken(token);
        const id: number = Number(decodedToken.sub);

        return new Promise<boolean>(resolve => {
            const request: HttpRequest<any> = this.getRequest(this.authLoginCtlrAdress, "GET", id);

            this.executeQuery<any>(request).subscribe(res => {
                this.storeDetails(res);
                this._isLogged.next(true);
                resolve(true);

            }, error => { resolve(false); }),
                error => { resolve(false); }
        });
    }

    protected storeDetails(details: any): void {
        this.currentUser = details.currentUser;
        this.menus = details.availableMenus;
        if (this.processDetails) this.processDetails(details);
    }

    hasRight(structurePath: string): boolean {
        let right = this._flattenMenus.find(f => f.path === structurePath);
        if (!right) return true;
        return right.isOwned !== false;
    }

    navigateToDefaultMenu(path?: string): void {
        let menus: Menu[] = [];
        if (this.menus) menus = [...this.menus];

        menus = menus.filter(m => m.isOwned !== false);
        if (path) menus = menus.filter(m => m.route === path);

        //Sort menus
        menus = menus.sort(this.sortSubMenus);

        if (menus.length) menus = menus[0].subMenus.filter(m => m.isOwned !== false);
        else menus = [];

        //Sort sub menus
        menus = menus.sort(this.sortSubMenus);

        if (menus.length) this.navigate(menus[0].structurePath);
        else this.navigate('/home');
    }

    private sortSubMenus(a: Menu, b: Menu): number {
        if (a.rank < b.rank) return -1;
        else if (a.rank > b.rank) return 1;
        else return 0;
    }

    private activateAutoLogout(maxInactivityTime?: number) {
        if (maxInactivityTime) this._minutesBeforeAutoLogout = maxInactivityTime;
        this._isAutoLogOut = true;
        this.resetInactivity();
        this.checkInactivity();
        this.initListener();
        this.initInterval();
    }

    deactivateAutoLogout() {
        this._isAutoLogOut = false;
    }

    initListener() {
        this._ngZone.runOutsideAngular(() => {
            document.body.addEventListener('click', () => this.resetInactivity());
        });
    }

    initInterval() {
        this._ngZone.runOutsideAngular(() => {
            setInterval(() => {
                this.checkInactivity();
            }, CHECK_INTERVALL);
        })
    }

    resetInactivity() {
        this.lastActivity = Date.now();
    }

    checkInactivity() {
        const now = Date.now();
        const timeleft = this.lastActivity + this._minutesBeforeAutoLogout * 60 * 1000;
        const diff = timeleft - now;
        const isTimeout = diff < 0;

        this._ngZone.run(() => {
            if (this._isAutoLogOut && isTimeout) {
                this.isLoggedIn().then(result => {
                    if (result) {

                        console.log(`You were automatically logged out after ${this._minutesBeforeAutoLogout} minutes of inactivity`);
                        this.logout();
                    }
                });
            }
        });
    }
}

